import { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CheckCircle2, Download, Sparkles } from "lucide-react";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import { API } from "@/lib/brand";

const GOALS = [
  { v: "home", label: "Owning a Home" },
  { v: "education", label: "Child's Education" },
  { v: "marriage", label: "Child's Marriage" },
  { v: "retirement", label: "Retirement" },
  { v: "travel", label: "Travel & Life Goals" },
];
const HORIZONS = [
  { v: "<5", label: "Under 5 years" },
  { v: "5-10", label: "5 – 10 years" },
  { v: "10-20", label: "10 – 20 years" },
  { v: "20+", label: "20+ years" },
];
const SAVE = [
  { v: "none", label: "Not investing yet" },
  { v: "<10", label: "Under 10%" },
  { v: "10-20", label: "10 – 20%" },
  { v: "20-30", label: "20 – 30%" },
  { v: "30+", label: "More than 30%" },
];
const LIFE = [
  { v: "none", label: "None" },
  { v: "<5x", label: "Below 5× annual income" },
  { v: "5-10x", label: "5 – 10× income" },
  { v: "10-15x", label: "10 – 15× income" },
  { v: "15x+", label: "15× income or more" },
];
const HEALTH = [
  { v: "none", label: "No health cover" },
  { v: "<5L", label: "Under ₹5 L" },
  { v: "5-10L", label: "₹5 – 10 L" },
  { v: "10-25L", label: "₹10 – 25 L" },
  { v: "25L+", label: "₹25 L or more" },
];
const EF = [
  { v: "none", label: "No buffer yet" },
  { v: "<3m", label: "Under 3 months of expenses" },
  { v: "3-6m", label: "3 – 6 months" },
  { v: "6-12m", label: "6 – 12 months" },
  { v: "12m+", label: "12 months or more" },
];

const STEPS = [
  { id: "goal", label: "Primary goal", question: "What are you primarily building toward?", options: GOALS, key: "primary_goal" },
  { id: "horizon", label: "Time horizon", question: "How far away is this goal?", options: HORIZONS, key: "horizon" },
  { id: "income", label: "Income", question: "Approximate monthly household income?", key: "monthly_income", type: "number", placeholder: "e.g. 150000" },
  { id: "save", label: "Savings", question: "About what portion of income do you invest each month?", options: SAVE, key: "save_rate" },
  { id: "life", label: "Life cover", question: "How much life cover do you currently have?", options: LIFE, key: "life_cover" },
  { id: "health", label: "Health cover", question: "Family health cover in place?", options: HEALTH, key: "health_cover" },
  { id: "ef", label: "Emergency fund", question: "Liquid emergency fund available?", options: EF, key: "emergency_fund" },
  { id: "contact", label: "You", question: "Where should we send your snapshot?", key: "contact" },
];

const INITIAL = {
  primary_goal: "",
  horizon: "",
  monthly_income: "",
  save_rate: "",
  life_cover: "",
  health_cover: "",
  emergency_fund: "",
  full_name: "",
  email: "",
  phone: "",
};

export default function Snapshot() {
  const [step, setStep] = useState(0);
  const [data, setData] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const cur = STEPS[step];
  const total = STEPS.length;
  const progress = ((step + (isStepComplete(cur, data) ? 1 : 0)) / total) * 100;

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const next = () => {
    if (!isStepComplete(cur, data)) {
      toast.error("Please choose an option to continue.");
      return;
    }
    if (step < total - 1) setStep(step + 1);
  };

  const prev = () => step > 0 && setStep(step - 1);

  const submit = async () => {
    if (!data.full_name.trim() || !data.email.trim()) {
      toast.error("Name and email are required to send your snapshot.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        full_name: data.full_name.trim(),
        email: data.email.trim(),
        phone: data.phone.trim() || null,
        primary_goal: data.primary_goal,
        horizon: data.horizon,
        monthly_income: Number(data.monthly_income) || 0,
        save_rate: data.save_rate,
        life_cover: data.life_cover,
        health_cover: data.health_cover,
        emergency_fund: data.emergency_fund,
      };
      const { data: res } = await axios.post(`${API}/snapshot`, payload);
      setResult(res);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const downloadPdf = async () => {
    try {
      const res = await axios.get(`${API}/snapshot/${result.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `reliserve-snapshot-${result.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Could not download PDF right now.");
    }
  };

  if (result) return <ResultScreen result={result} onDownload={downloadPdf} />;

  return (
    <div>
      <section className="border-b hairline">
        <div className="max-w-3xl mx-auto px-5 md:px-8 pt-14 md:pt-20 pb-8">
          <div className="flex items-center gap-2 chip mb-6" data-testid="snapshot-badge">
            <Sparkles size={12} /> PLANNING READINESS SNAPSHOT
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-ink">
            Where do you<br />actually <em className="text-brass">stand</em>?
          </h1>
          <p className="mt-6 text-[16px] text-ink-soft leading-relaxed max-w-2xl">
            Seven quick questions. In 30 seconds, we'll score your setup across savings, protection,
            and emergency preparedness — and email you a one-page PDF summary.
          </p>
        </div>
      </section>

      <section className="bg-paper-raised">
        <div className="max-w-3xl mx-auto px-5 md:px-8 py-10 md:py-14">
          {/* Progress */}
          <div className="mb-8" data-testid="snapshot-progress">
            <div className="flex items-center justify-between mb-2">
              <span className="eyebrow">
                Step {step + 1} of {total} · {cur.label}
              </span>
              <span className="num text-[12px] text-ink-soft">{Math.round(progress)}%</span>
            </div>
            <div className="h-[3px] bg-line overflow-hidden rounded-sm">
              <div
                className="h-full bg-ink transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          <div className="card-paper p-6 md:p-10" data-testid={`snapshot-step-${cur.id}`}>
            <h2 className="font-display text-2xl md:text-3xl text-ink leading-tight">
              {cur.question}
            </h2>

            {cur.type === "number" ? (
              <div className="mt-8">
                <label className="block">
                  <span className="eyebrow block mb-2">Amount (₹ / month)</span>
                  <input
                    type="number"
                    min={0}
                    value={data.monthly_income}
                    onChange={(e) => set("monthly_income", e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") next(); }}
                    placeholder={cur.placeholder}
                    className="fld num"
                    autoFocus
                    data-testid="snapshot-input-income"
                  />
                </label>
                <p className="text-[12px] text-ink-soft mt-3">
                  Approximate is fine — a rough number is enough for the snapshot.
                </p>
              </div>
            ) : cur.id === "contact" ? (
              <div className="mt-8 space-y-5">
                <label className="block">
                  <span className="eyebrow block mb-2">Full name *</span>
                  <input
                    type="text" required autoFocus
                    value={data.full_name}
                    onChange={(e) => set("full_name", e.target.value)}
                    className="fld" data-testid="snapshot-input-name"
                  />
                </label>
                <label className="block">
                  <span className="eyebrow block mb-2">Email *</span>
                  <input
                    type="email" required
                    value={data.email}
                    onChange={(e) => set("email", e.target.value)}
                    className="fld" data-testid="snapshot-input-email"
                  />
                </label>
                <label className="block">
                  <span className="eyebrow block mb-2">Phone (optional)</span>
                  <input
                    type="tel"
                    value={data.phone}
                    onChange={(e) => set("phone", e.target.value)}
                    className="fld" data-testid="snapshot-input-phone"
                  />
                </label>
                <p className="text-[12px] text-ink-soft leading-relaxed">
                  We'll email you a one-page PDF snapshot. No spam — one email, one attachment.
                </p>
              </div>
            ) : (
              <div className="mt-8 grid gap-3" role="radiogroup" data-testid="snapshot-options">
                {cur.options.map((opt) => {
                  const active = data[cur.key] === opt.v;
                  return (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => set(cur.key, opt.v)}
                      className={`text-left px-4 py-3.5 border rounded-lg transition-all flex items-center justify-between group ${
                        active
                          ? "border-ink bg-paper-raised"
                          : "border-line bg-paper hover:border-ink"
                      }`}
                      data-testid={`snapshot-opt-${cur.id}-${opt.v}`}
                    >
                      <span className={`text-[15px] ${active ? "text-ink font-medium" : "text-ink"}`}>
                        {opt.label}
                      </span>
                      <span
                        className={`w-4 h-4 rounded-full border transition-colors ${
                          active ? "border-ink bg-ink" : "border-line"
                        }`}
                      >
                        {active && <CheckCircle2 size={16} className="text-paper" />}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Nav */}
          <div className="mt-8 flex items-center justify-between">
            <button
              onClick={prev}
              disabled={step === 0}
              className={`btn-outline ${step === 0 ? "opacity-40 cursor-not-allowed" : ""}`}
              data-testid="snapshot-back"
            >
              <ArrowLeft size={14} /> Back
            </button>
            {step < total - 1 ? (
              <button onClick={next} className="btn-brass" data-testid="snapshot-next">
                Continue <ArrowRight size={14} />
              </button>
            ) : (
              <button onClick={submit} disabled={submitting} className="btn-brass" data-testid="snapshot-submit">
                {submitting ? "Generating…" : (<>Get My Snapshot <ArrowRight size={14} /></>)}
              </button>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function isStepComplete(step, data) {
  if (step.id === "contact") return true; // validated on submit
  if (step.type === "number") return String(data.monthly_income).trim() !== "" && Number(data.monthly_income) >= 0;
  return Boolean(data[step.key]);
}

function ResultScreen({ result, onDownload }) {
  const bandColor = useMemo(() => {
    if (result.total >= 70) return "text-[#14A5A5] border-[#14A5A5]";
    if (result.total >= 50) return "text-ink border-ink";
    return "text-[#DC2626] border-[#DC2626]";
  }, [result.total]);

  return (
    <div>
      <section className="border-b hairline">
        <div className="max-w-3xl mx-auto px-5 md:px-8 pt-14 md:pt-20 pb-16 text-center" data-testid="snapshot-result">
          <div className="chip mx-auto inline-flex mb-6">
            <CheckCircle2 size={12} /> SNAPSHOT READY
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-ink">
            Your readiness score
          </h1>
          <p className="mt-4 text-[15px] text-ink-soft max-w-xl mx-auto">
            A one-page PDF has been emailed to <span className="num text-ink">{result.email}</span>. You can also download it below.
          </p>

          <div className="mt-12 card-paper p-10 md:p-12 max-w-xl mx-auto">
            <div className="eyebrow mb-3">Overall score</div>
            <div className="flex items-end justify-center gap-2">
              <span className="num text-7xl md:text-8xl text-ink" data-testid="snapshot-total">{result.total}</span>
              <span className="num text-2xl text-ink-soft pb-4">/ 100</span>
            </div>
            <div className={`mt-6 inline-block px-4 py-1.5 border rounded-full text-[13px] font-medium ${bandColor}`} data-testid="snapshot-band">
              {result.band}
            </div>
          </div>

          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <button onClick={onDownload} className="btn-brass" data-testid="snapshot-download">
              <Download size={14} /> Download PDF
            </button>
            <a
              href="https://reliservepulse.com/book-consultation"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-outline"
              data-testid="snapshot-book"
            >
              Book a Consultation
            </a>
          </div>
          <a
            href="https://chat.whatsapp.com/GtdGWz5Ydj2D5zTn22qVBx?mode=gi_t"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 mt-6 text-[13.5px] text-brass-deep hover:text-brass transition-colors group"
            data-testid="snapshot-whatsapp-community"
          >
            <WhatsAppIcon size={14} />
            <span className="border-b border-brass/30 group-hover:border-brass transition-colors">
              Or join our WhatsApp community
            </span>
          </a>
        </div>
      </section>

      <section className="bg-paper-raised">
        <div className="max-w-3xl mx-auto px-5 md:px-8 py-16 md:py-20">
          <div className="eyebrow mb-3">Breakdown</div>
          <h2 className="font-display text-3xl md:text-4xl text-ink leading-tight">
            Where the score comes from.
          </h2>

          <div className="mt-10 space-y-4">
            {result.breakdown.map((b, i) => {
              const pct = (b.score / b.max_score) * 100;
              return (
                <div key={i} className="card-paper p-6" data-testid={`snapshot-breakdown-${i}`}>
                  <div className="flex items-center justify-between">
                    <div className="text-[15px] font-medium text-ink">{b.label}</div>
                    <div className="num text-[13px] text-ink-soft">
                      {b.score} <span className="text-ink-soft/70">/ {b.max_score}</span>
                    </div>
                  </div>
                  <div className="mt-3 h-[3px] bg-line rounded-sm overflow-hidden">
                    <div className="h-full bg-ink transition-all duration-700" style={{ width: `${pct}%` }} />
                  </div>
                  {b.note && (
                    <p className="mt-4 text-[13.5px] text-ink-soft leading-relaxed">{b.note}</p>
                  )}
                </div>
              );
            })}
          </div>

          <p className="mt-10 text-[12px] text-ink-soft/85 leading-relaxed">
            This snapshot is educational only and does not constitute financial, tax, or insurance
            advice. Actual figures depend on your specific circumstances. Speak with us to translate
            this into a full plan tailored to you.
          </p>
        </div>
      </section>
    </div>
  );
}
